import { Component, OnInit, inject, DestroyRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SolutionsService } from 'src/app/services/api/solutions.service';
import { Solution } from 'src/app/models/service/solution';
import { filter, finalize, mergeMap, switchMap, tap } from 'rxjs/operators';
import { BlueprintsService } from 'src/app/services/api/blueprints.service';
import { combineLatest, interval, Observable, Subscription } from 'rxjs';
import { Blueprint } from 'src/app/models/blueprints/blueprint';
import { InstancesService } from 'src/app/services/api/instances.service';
import { Instance } from 'src/app/models/instance/instance';
import { DeploymentsService } from 'src/app/services/api/deployments.service';
import { Deployment } from 'src/app/models/deploy/deployment';
import { DeployStatus } from 'src/app/models/deploy/deploy-status';
import { AuthService } from 'src/app/services/auth/auth.service';
import { RouterSegments } from 'src/app/models/router-segments';
import { BreadcrumbItem } from '../../../models/breadcrumb/breadcrumb-item';
import { translate } from '@jsverse/transloco';
import { Permission } from 'src/app/models/principal/permission';
import { MatDialog } from '@angular/material/dialog';
import { ServiceCardInfo } from 'src/app/models/service/service-card-info';
import { ServiceCardService } from 'src/app/services/util/service-card.service';
import { ErrorPageService } from '../../../services/util/error-page.service';
import { SidenavService } from 'src/app/services/util/sidenav.service';
import { Sandbox } from 'src/app/models/sandbox';
import { SolutionVersion } from 'src/app/models/service/solution-version';
import { PermissionsService } from '../../../services/util/permissions.service';
import { NotificationsService } from 'src/app/services/api/notifications.service';
import { Capability, CapabilityType } from '../../../models/service/capability';
import { ErrorDialogComponent } from 'src/app/components/error-dialog/error-dialog.component';
import { Title } from '@angular/platform-browser';
import { ServiceStatus } from 'src/app/models/service/service-status';
import { ToastrService } from 'src/app/services/util/toastr.service';
import { PermissionKeys } from 'src/app/models/principal/permission-keys';
import { Permissions } from 'src/app/models/principal/permission';

const UPDATED_REQUIRED_PERMISSIONS = [
  {
    key: PermissionKeys.SOLUTION_READ,
    action: Permissions.SOLUTION_READ
  },
];

const REQUIRED_PERMISSIONS = [
  Permission.CREATE_SANDBOX,
  Permission.CREATE_SERVICE_DEFINITION,
  Permission.VIEW_ENTERPRISE,
  Permission.NO_SERVICES
];

@Component({
    selector: 'app-service-summary',
    templateUrl: './service-summary.component.html',
    styleUrls: ['./service-summary.component.scss'],
    standalone: false
})
export class ServiceSummaryComponent implements OnInit {

  dataLoaded = false;

  service: Solution;
  servicesVersionsMap = new Map<string, SolutionVersion[]>();

  demoBlueprint: Blueprint;
  demoInstance: Instance;

  blueprints: Blueprint[];
  instances: Instance[];
  serviceCardInfos: ServiceCardInfo[];
  sandbox: Sandbox;

  RouterSegments = RouterSegments;
  tenant: string;

  breadcrumbItems: BreadcrumbItem[];

  creating = '&nbsp;&nbsp;&nbsp;';
  creatingAnimationInterval: Subscription;
  creatingCounter = 0;

  Permission = Permission;
  permissionsMap: {[key: string]: boolean};

  capabilities: any;
  selectedCapabilities: any[];
  updateSandbox = false;
  instanceId: any;
  updatingSandbox = false;
  displayStartFreeTrialButton: boolean;
  latestSolutionVersionId: any;
  serviceId: any;
  capabilityDescription: Capability[];
  descriptionArray: any;
  errorMessage: any;
  isInstance = true;
  solutionVersion: any;
  serviceVersions: any;
  sortedSolutionVersionId: any;
  updatedPermissionsMap: { [key: string]: boolean };
  mainArray: any;
  additionalIncluded = [];
  additionalOptional = [];
  includedCapabilities: any;
  optionalCapabilities: any;
  private destroyRef = inject(DestroyRef);

  constructor(private activatedRoute: ActivatedRoute, private router: Router, private solutionsService: SolutionsService,
              private blueprintsService: BlueprintsService, private instancesService: InstancesService,
              private deploymentsService: DeploymentsService, private authService: AuthService, private matDialog: MatDialog,
              private serviceCardService: ServiceCardService, private errorPageService: ErrorPageService,
              private sidenavService: SidenavService, private permissionsService: PermissionsService,
              private notificationsService: NotificationsService, private titleService: Title, private toastrService: ToastrService) {
  }

  ngOnInit(): void {
    this.tenant = this.authService.principal.provider;
    this.updatedPermissionsMap = this.permissionsService.resolveAPIPermissions(UPDATED_REQUIRED_PERMISSIONS);
    if (!this.updatedPermissionsMap[PermissionKeys.SOLUTION_READ]) {
      this.router.navigate(['/', this.tenant, RouterSegments.UNAUTHORIZED], {skipLocationChange: true});
    }
    this.subscribeForNotifications();
    this.permissionsMap = this.permissionsService.resolveUserPermissions(REQUIRED_PERMISSIONS);
    this.loadData();
  }

  loadData(): void {
    const serviceId = this.activatedRoute.snapshot.params.serviceId;
    combineLatest([this.solutionsService.getSolutionDetailsById(serviceId),
      this.solutionsService.getSolutionVersions(serviceId),
      this.loadBlueprints(serviceId),
      this.loadInstances(serviceId),
    ])
    .pipe(
      tap(async ([service, serviceVersions]) =>  {
        this.serviceVersions = serviceVersions;
        const promises = [];
        const latest = this.loadCapabilitiesDescription(serviceId, service.latestSolutionVersionId);
        promises.push(latest);
        await Promise.allSettled(promises).then(latestData => {
         // this.capabilities = latestData.map((version: any) => version.value)[0];
          this.service = service;
          this.titleService.setTitle(this.service?.name);
          this.servicesVersionsMap.set(this.service.id, serviceVersions);
          this.configureBreadcrumb(service);
          this.onDataLoaded();
        });
      }),
      finalize(() => {
        this.updateCreatingAnimation();
      }),
      takeUntilDestroyed(this.destroyRef)
    )
    .subscribe(() => {
      this.dataLoaded = true;
    }, error => {
      this.errorPageService.show404Page();
    });
  }

  loadCapabilitiesDescription(serviceId, latestSolutionVersionId): Promise<any> {
    return new Promise((resolve) => {
      this.solutionsService.getSolutionVersions(serviceId).pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(data => {
      this.sortedSolutionVersionId =
      (data.sort((a,b) => a?.version && b?.version ? b?.version?.localeCompare(a?.version) : !b?.version ? -1 : 1));
      // eslint-disable-next-line max-len
      this.solutionsService.getSolutionVersion(serviceId, (this.sortedSolutionVersionId ? this.sortedSolutionVersionId[0]?.id : latestSolutionVersionId))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(serviceDetails => {
        this.solutionVersion = serviceDetails;
        this.mainArray = this.solutionVersion.capabilities;
        this.solutionVersion?.capabilities?.forEach(element => {
          if(element?.nested) {
            element.hasNested = true;
            element?.nested.forEach(capablityiesDta => {
              this.mainArray = this.mainArray.filter(item => item?.name !== capablityiesDta);
            });
          }
        });
        this.mainArray.forEach(element => {
          let nestedObj = [];
          let obj = {};
          if(element?.nested) {
            element?.nested.forEach(innerElement=> {
              if (this.solutionVersion?.capabilities?.find(item => item.name === innerElement)) {
                obj = this.solutionVersion?.capabilities?.find(item => item.name === innerElement);
              } else {
                obj = {
                  name: innerElement
                };
              }
              nestedObj = [...nestedObj, obj];
            });
            element.nestedObj = nestedObj;
          }
        });

        this.includedCapabilities = this.mainArray?.filter(item => item?.required === CapabilityType.MANDATORY);
        this.optionalCapabilities = this.mainArray?.filter(item => item?.required === CapabilityType.OPTIONAL);
        if (!this.mainArray || this.mainArray?.length === 0) {
          this.includedCapabilities = [];
          this.optionalCapabilities = [];
        }
        this.capabilities = [...this.includedCapabilities, ...this.optionalCapabilities];
        this.capabilityDescription = serviceDetails.capabilities;
          resolve(serviceDetails.capabilities);
        }, (error) => {
        resolve(error);
      });
    });
  });
  }

  loadInstances(serviceId: string): Observable<Instance[]> {
    return this.instancesService.getInstancesForSolution(serviceId)
    .pipe(tap({
      next:(instances) => {
        this.instances = instances;
        for(const instanceName of this.instances) {
          if(this.instances && instanceName.expirationDate) {
          this.displayStartFreeTrialButton = true;
          }
        }
        this.instanceId = this.instances.length > 0 ? this.instances.find(instance => instance.id).id : '';
      },
      error: () => {
        this.isInstance = false;
      }
    }));
  }

  loadBlueprints(serviceId: string): Observable<Blueprint[]> {
    return this.blueprintsService.getBlueprintsForSolution(serviceId)
    .pipe(tap((blueprints: any) => {
      this.blueprints = blueprints;
      this.demoBlueprint = blueprints.find(blueprint => blueprint.demo);
      if(this.demoBlueprint?.capabilities) {
        this.selectedCapabilities = this.demoBlueprint.capabilities;
      }
    }));
  }

  createInstance(): void {
    const getSelectedCapabilities = this.getSelectedCapabilities();
    this.demoInstance = {
      status: DeployStatus.DEPLOYING
    } as Instance;

    const deployment = {
      solutionId: this.service.id,
      region: this.service.regionalAvailability[0],
      name: 'Demo Instance',
      capabilities: getSelectedCapabilities
    } as Deployment;

    this.deploymentsService.createAndDeployDemoInstance(deployment)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.loadData();
      }, (error) => {
        this.matDialog.open(ErrorDialogComponent, {
          minHeight: '342px',
          width: '566px',
          panelClass: 'flex-dialog',
          data: {
            title: translate('NOTIFICATIONS.INSTANCE_FAILED'),
            description: error,
          }
        });
        // Remove demoInstance if deployment fail to avoid disabling the button.
        delete this.demoInstance;
      });
  }

  updateInstance(): void {
    this.updatingSandbox = true;
    const getSelectedCapabilities = this.getSelectedCapabilities();

    const deployment = {
      id: this.instanceId,
      capabilities: getSelectedCapabilities
    } as Deployment;

    this.deploymentsService.updateCapabilities(deployment)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
          this.loadData();
          this.updatingSandbox = false;
      });
  }

  getSelectedCapabilities(): any[] {
    const selectedCapabilities = [];
    if(this.capabilities && this.capabilities.length > 0){
      this.capabilities.filter((x)=> {
        if(x.selected && x.required === CapabilityType.OPTIONAL){
          selectedCapabilities.push(x.name);
        }
      });
    }
    return selectedCapabilities;
  }

  blueprintCreated(createdBlueprint): void {
    this.blueprints.push(createdBlueprint);
    this.serviceCardInfos = this.serviceCardService.toServiceCardInfos(
      [this.service], this.blueprints, this.instances, this.servicesVersionsMap)
      .filter(cardInfo => !cardInfo.blueprint.demo || cardInfo.instances.length);

    this.router.navigate(['/', this.tenant, RouterSegments.SERVICES, this.service.id, RouterSegments.BLUEPRINTS, createdBlueprint.id]);
  }

  setupService(): void {
    this.router.navigate(['/', this.tenant, RouterSegments.SERVICES, this.service.id, RouterSegments.SERVICE_DEFINITION]);
  }

  onBlueprintDeleted(cardInfo: ServiceCardInfo): void {
    this.serviceCardInfos.splice(this.serviceCardInfos.indexOf(cardInfo), 1);
  }

  private async onDataLoaded(): Promise<void> {
    if (this.demoBlueprint) {
      this.demoInstance = this.instances && this.instances.find(instance => instance.blueprintId === this.demoBlueprint.id);
    }

    this.serviceCardInfos = this.serviceCardService.toServiceCardInfos([this.service], this.blueprints, this.instances,
      this.servicesVersionsMap).filter(cardInfo => !cardInfo.blueprint.demo || cardInfo.instances.length);
  }

  private updateCreatingAnimation(): void {
    if (this.demoInstance && this.demoInstance.status === DeployStatus.DEPLOYING) {
      if (!this.creatingAnimationInterval) {
        this.creatingAnimationInterval = interval(700)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(() => {
            this.creating = '.'.repeat(this.creatingCounter) + '&nbsp;'.repeat(3 - this.creatingCounter);
            this.creatingCounter += 1;
            this.creatingCounter %= 3 + 1;
          });
      }
      setTimeout(() => {
        this.loadData();
      }, 60000);
    } else {
      if (this.creatingAnimationInterval) {
        this.creatingAnimationInterval.unsubscribe();
        this.creatingAnimationInterval = null;
        this.creatingCounter = 0;
      }
    }
  }

  private configureBreadcrumb(service: Solution): void {
    this.breadcrumbItems = [];
    this.breadcrumbItems.push({ label: 'Home', url: [] });
    // this.breadcrumbItems.push({label: translate('CATALOGUE.CATALOGUE'), url: ['catalogue']});
    // this.breadcrumbItems.push({label: service.name });
  }

  private createBlueprint(blueprint: Blueprint): void {
    this.blueprintsService.createBlueprint(blueprint)
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe((createdBlueprint) => {
      if (createdBlueprint) {
        this.blueprintCreated(createdBlueprint);
      }
    });
  }

  private subscribeForNotifications(): void {
    this.notificationsService.onNotification({ type: 'com.temenos.engine.instance'})
    .pipe(
      filter(notification => notification.data?.solutionId === this.service?.id),
      mergeMap(() => this.loadBlueprints(this.service.id)),
      mergeMap(() => this.loadInstances(this.service.id)),
      takeUntilDestroyed(this.destroyRef))
    .subscribe(() => {
      this.onDataLoaded();
    });
  }
}
